// @ts-nocheck
import { supabase } from '@/integrations/supabase/client';
import { EmailNotificationService } from './emailNotificationService';
import { InstantEmailService } from './instantEmailService';

export class NotificationHandler {
  private static isInitialized = false;

  static initialize() {
    if (this.isInitialized) return;
    
    // Set up real-time listeners for database changes
    this.setupBidNotificationListener();
    this.setupManualBidNotificationListener();
    this.setupProxyBidNotificationListener();
    this.setupOutbidNotificationListener();
    
    this.isInitialized = true;
    console.log('✅ NotificationHandler initialized');
  }

  private static setupBidNotificationListener() {
    supabase
      .channel('bid-notifications')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'bids',
          filter: 'status=eq.approved'
        },
        async (payload) => {
          console.log('📧 Bid approved notification triggered:', payload);
          await this.handleBidApprovalNotification(payload.new);
        }
      )
      .subscribe();
  }

  private static setupManualBidNotificationListener() {
    supabase
      .channel('manual-bid-notifications')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'manual_bids',
          filter: 'status=eq.approved'
        },
        async (payload) => {
          console.log('📧 Manual bid approved notification triggered:', payload);
          await this.handleBidApprovalNotification(payload.new);
        }
      )
      .subscribe();
  }

  private static setupProxyBidNotificationListener() {
    supabase
      .channel('proxy-bid-notifications')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'proxy_bids',
          filter: 'status=eq.active'
        },
        async (payload) => {
          console.log('📧 Proxy bid approved notification triggered:', payload);
          await this.handleProxyBidApprovalNotification(payload.new);
        }
      )
      .subscribe();
  }

  private static setupOutbidNotificationListener() {
    supabase
      .channel('outbid-notifications')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'lots'
        },
        async (payload) => {
          console.log('📧 Lot updated, checking for outbid notification:', payload);
          // Check if current_bid changed
          if (payload.old?.current_bid !== payload.new?.current_bid) {
            await this.handleOutbidNotification(payload.old, payload.new);
          }
        }
      )
      .subscribe();
  }

  private static async handleBidApprovalNotification(bid: any) {
    try {
      // Get user and lot information
      const { data: user, error: userError } = await supabase
        .from('profiles')
        .select('email, first_name, last_name')
        .eq('id', bid.user_id)
        .single();

      const { data: lot, error: lotError } = await supabase
        .from('lots')
        .select(`
          *,
          artwork:artwork_id (
            title,
            artist:artist_id (name),
            image_url
          ),
          auction:auction_id (
            title
          )
        `)
        .eq('id', bid.lot_id)
        .single();

      if (!userError && !lotError && user && lot && (user as any)?.email) {
        const userObj = user as any;
        const lotObj = lot as any;
        const userName = `${userObj.first_name || ''} ${userObj.last_name || ''}`.trim() || 'Valued Customer';
        
        await EmailNotificationService.sendBidApprovalNotification({
          userEmail: (user as any).email,
          userId: bid.user_id,
          userName,
          artworkTitle: (lot as any).artwork?.title || 'Unknown',
          artistName: (lot as any).artwork?.artist?.name || 'Unknown',
          lotNumber: (lot as any).lot_number,
          bidAmount: bid.amount,
          auctionTitle: (lot as any).auction?.title || 'Unknown',
          lotId: (lot as any).id,
          artworkImage: (lot as any).artwork?.image_url
        });
      }
    } catch (error) {
      console.error('Error handling bid approval notification:', error);
    }
  }

  private static async handleProxyBidApprovalNotification(proxyBid: any) {
    try {
      // Get user and lot information
      const { data: user } = await supabase
        .from('profiles')
        .select('email, first_name, last_name')
        .eq('id', proxyBid.user_id)
        .single();

      const { data: lot } = await supabase
        .from('lots')
        .select(`
          *,
          artwork:artwork_id (
            title,
            artist:artist_id (name),
            image_url
          ),
          auction:auction_id (
            title
          )
        `)
        .eq('id', proxyBid.lot_id)
        .single();

      if (user && lot && user.email) {
        const userName = `${user.first_name || ''} ${user.last_name || ''}`.trim() || 'Valued Customer';
        
        await EmailNotificationService.sendProxyBidApprovalNotification({
          userEmail: user.email,
          userId: proxyBid.user_id,
          userName,
          artworkTitle: lot.artwork?.title || 'Unknown',
          artistName: lot.artwork?.artist?.name || 'Unknown',
          lotNumber: lot.lot_number,
          maxBidAmount: proxyBid.max_bid,
          auctionTitle: lot.auction?.title || 'Unknown',
          lotId: lot.id,
          artworkImage: lot.artwork?.image_url
        });
      }
    } catch (error) {
      console.error('Error handling proxy bid approval notification:', error);
    }
  }

  private static async handleOutbidNotification(oldLot: any, newLot: any) {
    try {
      // Guard against undefined current_bid
      if (oldLot.current_bid === undefined || oldLot.current_bid === null) {
        console.log('📧 Outbid notification: No previous current_bid to check against');
        return;
      }

      // Find the previous high bidder
      const { data: previousBids } = await supabase
        .from('bids')
        .select('user_id, amount, created_at')
        .eq('lot_id', newLot.id)
        .eq('status', 'approved')
        .eq('amount', oldLot.current_bid)
        .order('created_at', { ascending: false })
        .limit(1);

      const { data: previousManualBids } = await supabase
        .from('manual_bids')
        .select('user_id, amount, created_at')
        .eq('lot_id', newLot.id)
        .eq('status', 'approved')
        .eq('amount', oldLot.current_bid)
        .order('created_at', { ascending: false })
        .limit(1);

      const allPreviousBids = [...(previousBids || []), ...(previousManualBids || [])];
      const previousHighBidder = allPreviousBids.sort((a, b) => 
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      )[0];

      if (previousHighBidder) {
        // Get user and lot information
        const { data: user } = await supabase
          .from('profiles')
          .select('email, first_name, last_name')
          .eq('id', previousHighBidder.user_id)
          .single();

        const { data: lot } = await supabase
          .from('lots')
          .select(`
            *,
            artworks:artwork_id (
              title,
              artists:artist_id (name),
              image_url
            ),
            auctions:auction_id (
              title
            )
          `)
          .eq('id', newLot.id)
          .single();

        if (user && lot && user.email) {
          const userName = `${user.first_name || ''} ${user.last_name || ''}`.trim() || 'Valued Customer';
          
          await EmailNotificationService.sendOutbidNotification({
            userEmail: user.email,
            userId: previousHighBidder.user_id,
            userName,
            artworkTitle: lot.artworks?.title || 'Unknown',
            artistName: lot.artworks?.artists?.name || 'Unknown',
            lotNumber: lot.lot_number,
            yourBid: oldLot.current_bid,
            newHighBid: newLot.current_bid,
            auctionTitle: lot.auctions?.title || 'Unknown',
            lotId: lot.id,
            artworkImage: lot.artworks?.image_url
          });
        }
      }
    } catch (error) {
      console.error('Error handling outbid notification:', error);
    }
  }

  static async sendWelcomeEmail(userId: string, userEmail: string, userName: string) {
    try {
      await EmailNotificationService.sendWelcomeNotification({
        userEmail,
        userId,
        userName
      });
    } catch (error) {
      console.error('Error sending welcome email:', error);
    }
  }

  static async sendBidConfirmationEmail(data: {
    userEmail: string;
    userId: string;
    userName: string;
    artworkTitle: string;
    artistName: string;
    lotNumber: string;
    bidAmount: number;
    currentHighBid: number;
    auctionTitle: string;
    auctionEndTime: string;
    lotId: string;
    artworkImage?: string;
  }) {
    try {
      await EmailNotificationService.sendBidConfirmationNotification(data);
    } catch (error) {
      console.error('Error sending bid confirmation email:', error);
    }
  }

  static async sendProxyBidSetupConfirmationEmail(data: {
    userEmail: string;
    userId: string;
    userName: string;
    artworkTitle: string;
    artistName: string;
    lotNumber: string;
    maxBidAmount: number;
    auctionTitle: string;
    lotId: string;
    artworkImage?: string;
  }) {
    try {
      await EmailNotificationService.sendProxyBidSetupConfirmation(data);
    } catch (error) {
      console.error('Error sending proxy bid setup confirmation email:', error);
    }
  }
} 